/**
 * Component OrderHistoryGrid is used to show pagination and manage page changes for the specified store.
 *
 * @attr {String} [creation-date-label] - The grid header label for the order creation date
 * @attr {String} [order-nr-label] - The grid header label for the order number
 * @attr {String} [delivery-address-label] - The grid header label for the order delivery address
 * @attr {String} [total-label] - The grid header label for the order total
 * @attr {String} [total-points-label] - The grid header label for the order total points
 * @attr {String} [status-label] - The grid header label for the order status
 * @attr {String} [view-details-label] - The label of the link to the order details page
 * @attr {String} [tracking-link-label] - The label of the link to the postnl
 * @attr {String} order-details-url - Url prefix for order details page
 * @attr {Object} [order-status-labels] - Url prefix for order details page
 *
 * @param {Boolean} anyLoyalty - getter if order have loyalty products
 * @param {Array} orders - orders info
 *
 * HTML Structure Example
 *
 * <e2-order-history-grid
 *      creation-date-label="Order date"
 *      order-nr-label="Order number"
 *      delivery-address-label="Delivery address"
 *      total-label="Total price"
 *      total-points-label="Total points"
 *      status-label="Status of the order"
 *      view-details-label="View order >"
 *      order-details-url="/my-account/order/"
 *      tracking-link-label="/my-account/order/"
 *      order-status-labels='{"notShipped":"Nicht ausgeliefert"}'
 * >
 *     <div v-slot="pagination"> Pagination here </div>
 *     <div v-slot="count"> Results per page here </div>
 * </e2-order-history-grid>
 */

import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { LightLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { actions, selectors } from '@elab/core/store/orders';
import { actions as globalActions } from '@elab/core/store/global';
import { getFormattedPrice } from '@elab/core/utils/PriceUtils';
import { getFormattedAddress } from '@elab/core/utils/cart/CartUtils';
import { formatFromISO } from '@elab/core/utils/DateUtils';
import { cartTypeConstants } from '@elab/core/utils/constants';
import { link } from '@elab/core/utils/htmlTemplates';
import { selectors as uiSelectors } from '@elab/core/store/ui';

class OrderHistoryGrid extends LightLitElement {
    static get properties() {
        return {
            creationDateLabel: {
                type: String,
                attribute: 'creation-date-label',
            },
            dateFormat: { type: String, attribute: 'date-format' },
            orderNrLabel: { type: String, attribute: 'order-nr-label' },
            deliveryAddressLabel: {
                type: String,
                attribute: 'delivery-address-label',
            },
            quantityLabel: { type: String, attribute: 'quantity-label' },
            quantityDescription: {
                type: String,
                attribute: 'quantity-description',
            },
            totalLabel: { type: String, attribute: 'total-label' },
            totalPointsLabel: { type: String, attribute: 'total-points-label' },
            statusLabel: { type: String, attribute: 'status-label' },
            orderStatusLabels: {
                type: Object,
                attribute: 'order-status-labels',
            },
            digitalLabel: {
                type: String,
                attribute: 'digital-label',
            },
            statusSettings: { type: Boolean, attribute: 'status-settings' },
            viewDetailsLabel: { type: String, attribute: 'view-details-label' },
            moreDetailsLabel: { type: String, attribute: 'more-details-label' },
            orderDetailsUrl: { type: String, attribute: 'order-details-url' },
            collectionReceiptLabel: {
                type: String,
                attribute: 'collection-receipt-label',
            },
            trackingLinkLabel: {
                type: String,
                attribute: 'tracking-link-label',
            },
            disabledFeatures: { type: Array, attribute: 'disabled-features' },
            additionalFeatures: { type: Array, attribute: 'additional-features' },
            pageSize: { type: Number, attribute: 'page-size' },
            anyLoyalty: { type: Boolean, attribute: false },
            linkOnDate: { type: Boolean, attribute: 'link-on-date' },
            orders: { type: Array, attribute: false },
            analyticsConfig: { type: String, attribute: 'analytics-config' },
            offlineOrders: { type: Boolean, attribute: 'offline-orders' },
            isOfflineTabOpened: { type: Boolean, attribute: false },
            isOfflineLoaded: { type: Boolean, attribute: false },
            itemIconClass: { type: String, attribute: 'item-icon' },
            hidePoints: { type: Boolean, attribute: 'hide-point' },
        };
    }

    constructor() {
        super();
        this.orders = [];
        this.dateFormat = 'DD-MM-YYYY';
        this.orderNrLabel = '';
        this.orderDetailsUrl = '';
        this.viewDetailsLabel = '';
        this.moreDetailsLabel = '';
        this.digitalLabel = '';
        this.statusSettings = false;
        this.quantityLabel = '';
        this.quantityDescription = '';
        this.collectionReceiptLabel = '';
        this.trackingLinkLabel = '';
        this.disabledFeatures = [];
        this.additionalFeatures = [];
        this.pageSize = '';
        this.analyticsConfig = '';
        this.linkOnDate = false;
        this.offlineOrders = false;
        this.isOfflineTabOpened = false;
        this.isOfflineLoaded = false;
        this.itemIconClass = '';
        this.hidePoints = false;
    }

    afterConnect() {
        if (!this.offlineOrders) {
            this.dispatchEvent(new CustomEvent('load-orders'));
        }
    }

    updated(changedProperties) {
        if (
            changedProperties.has('isOfflineTabOpened') &&
            this.isOfflineTabOpened &&
            !this.isOfflineLoaded
        ) {
            this.dispatchEvent(new CustomEvent('load-orders'));
            this.isOfflineLoaded = true;
        }
    }

    featureEnabled(featureName) {
        return !this.disabledFeatures.find((feature = '') => feature.trim() === featureName);
    }

    additionalFeatureEnabled(featureName) {
        return !!this.additionalFeatures.find((feature = '') => feature.trim() === featureName);
    }

    setTrackingLink(trackingUrl) {
        return html`
            <a class="simple-link d-block" href="${trackingUrl}" target="_blank">
                ${this.trackingLinkLabel}
            </a>
        `;
    }

    getRedeemLoyaltyPoints(points = '-') {
        return points === 0 ? '-' : points;
    }

    getOrderHistoryClasses(rowType) {
        const map = {
            'table-item-content-row': true,
            [`table-item-content-row--${rowType}`]: rowType,
        };
        return classMap(map);
    }

    getOrderRefTemplate(code, statusDisplay, documents) {
        return this.featureEnabled('orderRef')
            ? html`
                  <e2-order-history-order-number
                      .code="${code}"
                      order-nr-label="${this.orderNrLabel}"
                      order-details-url="${this.orderDetailsUrl}"
                      view-details-label="${this.viewDetailsLabel}"
                      .status-display="${statusDisplay}"
                      .documents="${documents}"
                      class="${this.analyticsConfig !== ''
                          ? ''
                          : this.getOrderHistoryClasses('number')}"
                  ></e2-order-history-order-number>
              `
            : null;
    }

    getAddressTemplate(deliveryModeName, addressFirstLine, addressSecondLine, trackingUrls) {
        return this.featureEnabled('address')
            ? html`
                  <div class="${this.getOrderHistoryClasses()}">
                      <div class="table-item-label">
                          ${this.deliveryAddressLabel}
                      </div>
                      <div class="table-item-text">
                          ${deliveryModeName == 'loyalty'
                              ? ''
                              : html`
                                    <strong>${deliveryModeName}</strong>
                                    <div>${addressFirstLine}</div>
                                    <div>${addressSecondLine}</div>
                                    ${trackingUrls.map(trackingUrl =>
                                        this.setTrackingLink(trackingUrl),
                                    )}
                                `}
                      </div>
                  </div>
              `
            : null;
    }

    getQuantityTemplate(qty) {
        return this.featureEnabled('quantity')
            ? html`
                  <div class="${this.getOrderHistoryClasses('qty')}">
                      <div class="table-item-label">${this.quantityLabel}</div>
                      <div class="table-item-text">
                          ${qty} <span>${this.quantityDescription}</span>
                      </div>
                  </div>
              `
            : null;
    }

    getTotalPriceTemplate(total) {
        return this.featureEnabled('price')
            ? html`
                  <div class="${this.getOrderHistoryClasses('price')}">
                      <div class="table-item-label">${this.totalLabel}</div>
                      <div class="table-item-text table-item-text__price">
                          ${getFormattedPrice(total)}
                      </div>
                  </div>
              `
            : null;
    }

    getTotalPointsTemplate(redeemLoyaltyPoints) {
        return this.anyLoyalty && this.featureEnabled('points')
            ? html`
                  <div class="${this.getOrderHistoryClasses()}">
                      <div class="table-item-label">
                          ${this.totalPointsLabel}
                      </div>
                      <div class="table-item-text__price">
                          <strong>${this.getRedeemLoyaltyPoints(redeemLoyaltyPoints)}</strong>
                      </div>
                  </div>
              `
            : null;
    }

    getStatusTemplate(statusDisplay) {
        return this.featureEnabled('status')
            ? html`
                  <div class="${this.getOrderHistoryClasses('status')}">
                      <div class="table-item-label">${this.statusLabel}</div>
                      <div class="table-item-text">
                          <div
                              class="table-item-text-with-icon
                                  ${this.statusSettings ? `table-item-text-${statusDisplay}` : ''}"
                          >
                              ${this.orderStatusLabels[statusDisplay]}

                              <e2-tooltip
                                  show-on-hover
                                  class="table-item-text-title-icon table-item-tooltip"
                              >
                                  <i
                                      tabindex="1"
                                      class="icon icon-info tooltip-icon"
                                      v-slot="target"
                                  ></i>
                                  <div v-slot="content">
                                      Display additional delivery information when the customer
                                      hovers on the tooltip
                                  </div>
                              </e2-tooltip>
                          </div>
                      </div>
                  </div>
              `
            : null;
    }

    getMoreDetailsTemplate(code) {
        return this.featureEnabled('details')
            ? html`
                  <div class="${this.getOrderHistoryClasses('details')}">
                      ${link(
                          {
                              className: 'simple-link',
                              href: `${this.orderDetailsUrl}${code}`,
                          },
                          this.moreDetailsLabel ? this.moreDetailsLabel : '',
                      )}
                  </div>
              `
            : null;
    }

    getOfflineStoreDetailsTemplate(order) {
        return this.additionalFeatureEnabled('offlineStoreDetails') &&
            order &&
            order.deliveryAddress
            ? html`
                  <div class="${this.getOrderHistoryClasses('offline-store-details')}">
                      <div class="table-item-text">
                          <strong>${order.deliveryAddress.firstName}</strong>
                          <div class="address-line">
                              ${order.deliveryAddress.formattedAddress}
                          </div>
                      </div>
                  </div>
              `
            : null;
    }

    getOfflinePointsTemplate(order) {
        return this.additionalFeatureEnabled('offlinePoints')
            ? html`
                  <div class="${this.getOrderHistoryClasses('offline-points')}">
                      <div class="table-item-text">
                          <strong>${order.loyaltyPointsEarned}</strong>
                      </div>
                  </div>
              `
            : null;
    }

    rowTemplate = (order = {}) => {
        const {
            code,
            placed,
            deliveryModeName,
            deliveryAddress = {},
            total,
            redeemLoyaltyPoints,
            totalUnitCount,
            statusDisplay,
            documents,
            trackingUrls = [],
            cartType = '',
        } = order;
        const { addressFirstLine, addressSecondLine } = getFormattedAddress(
            deliveryAddress.formattedAddress,
        );

        const orderRef = this.getOrderRefTemplate(code, statusDisplay, documents);

        return html`
            <div class="table-item">
                ${this.itemIconClass
                    ? html`
                          <i class="${this.itemIconClass}"></i>
                      `
                    : ''}
                <e2-accordion
                    active-md
                    active-sm
                    default-open
                    class="table-item-container"
                    add-class="accordion-container--order-history"
                    arrow-open-classes="icon icon-arrow order-history__icon order-history__icon--opened"
                    arrow-close-classes="icon icon-arrow order-history__icon"
                >
                    <div class="table-item-header" v-slot="title">
                        <div class="table-item-header-wrapper">
                            <p class="table-item-header-wrapper-text">
                                ${code}
                            </p>
                            <h3 class="table-item-header-wrapper-title">
                                ${this.orderStatusLabels[statusDisplay]}
                            </h3>
                        </div>
                    </div>

                    <div class="table-item-section">
                        ${cartType === cartTypeConstants.DIGITAL && this.digitalLabel
                            ? html`
                                  <p class="table-item-section--digital-label">
                                      ${this.digitalLabel}
                                  </p>
                              `
                            : ''}
                        <div class="table-item-content" v-slot="content">
                            <div class="${this.getOrderHistoryClasses('date')}">
                                <div class="table-item-label">
                                    ${this.creationDateLabel}
                                </div>
                                <div class="table-item-text">
                                    ${formatFromISO(placed, this.dateFormat)}
                                </div>
                            </div>

                            ${this.analyticsConfig !== '' && this.featureEnabled('orderRef')
                                ? html`
                                      <e2-click-tracking
                                          analytics-config="${this.analyticsConfig}"
                                          type="account_orders-number"
                                          class="${this.getOrderHistoryClasses('number')}"
                                      >
                                          ${orderRef}
                                      </e2-click-tracking>
                                  `
                                : orderRef}
                            ${this.getAddressTemplate(
                                deliveryModeName,
                                addressFirstLine,
                                addressSecondLine,
                                trackingUrls,
                            )}
                            ${this.getQuantityTemplate(totalUnitCount)}
                            ${this.getTotalPriceTemplate(total)}
                            ${this.getTotalPointsTemplate(redeemLoyaltyPoints)}
                            ${this.getStatusTemplate(statusDisplay)}
                            ${this.getOfflineStoreDetailsTemplate(order)}
                            ${this.getOfflinePointsTemplate(order)}
                            ${this.getMoreDetailsTemplate(code)}
                        </div>
                    </div>
                </e2-accordion>
            </div>
        `;
    };

    render() {
        return html`
            <div class="orders">
                <div class="table">
                    <v-slot class="table-header" name="header"></v-slot>
                    ${this.orders.map(this.rowTemplate)}
                </div>
                <v-slot name="pagination"></v-slot>
                <v-slot name="load-more"></v-slot>
            </div>
        `;
    }
}

const mapDispatchToEvents = (dispatch, ctx) => ({
    'load-orders': () =>
        dispatch(actions.loadOrders({ size: ctx.pageSize, type: !!ctx.offlineOrders })),
    redirect: e => dispatch(globalActions.redirect(e.detail)),
});

const mapStateToProps = (state, ctx) => ({
    orders: ctx.offlineOrders ? selectors.getPaginatedOrders(state) : selectors.getOrders(state),
    anyLoyalty: selectors.getAnyLoyalty(state),
    isOfflineTabOpened: uiSelectors.getTabName(state) === 'orders-offline',
});

export default () =>
    customElements.define(
        'e2-order-history-grid',
        connect(mapStateToProps, mapDispatchToEvents)(OrderHistoryGrid),
    );
